import { parseUtmParams, type BookingUtmParams } from "@openings/booking-core";

/**
 * Client-side UTM capture for booking attribution.
 *
 * UTM params usually live on the landing URL (e.g. a "Reserve with Google"
 * link), but a booking can be completed several navigations later. We persist
 * the first UTM set we see for the session so the attribution survives until
 * the appointment is actually created.
 */
const STORAGE_KEY = "openings:booking-utm";

function readStoredUtm(): BookingUtmParams {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as BookingUtmParams;
    return {
      source: parsed.source ?? undefined,
      medium: parsed.medium ?? undefined,
      campaign: parsed.campaign ?? undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Read UTM params from the current URL. If present, persist them for the rest
 * of the session and return them; otherwise fall back to any previously
 * persisted set. Safe to call repeatedly and during SSR (returns `{}`).
 */
export function captureBookingUtm(): BookingUtmParams {
  if (typeof window === "undefined") return {};
  const fromUrl = parseUtmParams(window.location.search);
  if (fromUrl.source || fromUrl.medium || fromUrl.campaign) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    } catch {
      /* sessionStorage unavailable (private mode) — ignore */
    }
    return fromUrl;
  }
  return readStoredUtm();
}
